import {useCallback, useEffect, useMemo, useRef, useState} from "react";

// Converts a datetime-local string ("2026-06-20T10:30") to RFC3339 with the
// browser's local timezone offset ("2026-06-20T10:30:00+01:00"), so the backend
// schedules at the user's local time rather than treating it as UTC.
function toRFC3339WithOffset(datetimeLocal: string): string {
    if (!datetimeLocal) return datetimeLocal;
    const d = new Date(datetimeLocal);
    if (isNaN(d.getTime())) return datetimeLocal;
    const pad = (n: number) => String(n).padStart(2, "0");
    const off = -d.getTimezoneOffset();
    const sign = off >= 0 ? "+" : "-";
    const hh = pad(Math.floor(Math.abs(off) / 60));
    const mm = pad(Math.abs(off) % 60);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().replace("Z", `${sign}${hh}:${mm}`).replace(/\.\d{3}/, "");
}
import {useSiren} from "../SirenContext";
import {platformAPI} from "../../../api/axiosConfig";
import {useNavigateWithClientID} from "./useNavigateWithClientID";
import {isAxiosError} from "axios";
import {DeliveryMethod, MassWarningTemplate, ZoneTimeRange} from "../typedefs";
import {StagedUpload} from "../components/MultiFileUpload";
import {useGolemErrors} from "../../../lib/golem/useGolemErrors";
import {Shape} from "../../../types/shapes";
import {IntervalFormat} from "../components/IntervalInput";
import {useToggle} from "usehooks-ts";
import {ValidationResult} from "react-querybuilder";
import {resolveSirenDefaultBroadcastConfig} from "../../../features/management-v2/hooks/useGetSirenDefaultBroadcastConfigs";
import {resolveSirenDefaultDeliveryMethods} from "../../../features/management-v2/hooks/useGetSirenDefaultDeliveryMethods";


type UseCreateTemplateProps = Partial<MassWarningTemplate>





export function useCreateTemplate(props?: UseCreateTemplateProps, mode?: string, initialContactIDs?: string[]) {

    const hasInitialContacts = initialContactIDs && initialContactIDs.length > 0;

    const initialName = useMemo(() => {
        if (mode === "alert" && props?.name) {
            const date = new Date();
            const dd = String(date.getDate()).padStart(2, "0");
            const mm = String(date.getMonth() + 1).padStart(2, "0");
            const yyyy = date.getFullYear();
            return `${props.name} ${dd}/${mm}/${yyyy}`;
        }
        return props?.name ?? "";
    }, []);
    const [name, setName] = useState<string>(initialName)
    const [category, setCategory] = useState<string>(props?.category ?? "")
    const [content, setContent] = useState<string>(props?.content ?? "")
    const [type, setType] = useState<string | undefined>(props?.type ?? "message")
    const [departmentID, setDepartmentID] = useState<string | undefined>(props?.departmentID ?? "")
    const [responseOptions, setResponseOptions] = useState<string[]>(props?.responseOptions ?? [])

    const [smsContent, setSMSContent] = useState<string>(props?.smsContent ?? "")
    const [useSeparateSMSContent, setUseSeparateSMSContent] = useState<boolean>(props?.useSeparateSMSContent ?? false)
    const [smsResponseMode, setSMSResponseMode] = useState<string | undefined>(props?.smsResponseMode ?? "dynamic_link")

    const [speechMode, setSpeechMode] = useState<string | undefined>(props?.speechMode ?? "")
    const [audioFileID, setAudioFileID] = useState<string | undefined>(props?.audioFileID ?? undefined)
    const [audioFileContainsIVR, setAudioFileContainsIVR] = useState(props?.audioFileContainsIVR ?? false)

    const [contactIDs, setContactIDs] = useState<string[]>(
        hasInitialContacts ? initialContactIDs! : props?.contactIDs ?? []
    );
    const [contactGroupIDs, setContactGroupIDs] = useState<string[]>(
        hasInitialContacts ? [] : props?.contactGroupIDs ?? []
    );
    const [facilityIDs, setFacilityIDs] = useState<string[]>(
        hasInitialContacts ? [] : props?.facilityIDs ?? []
    );
    const [zones, setZones] = useState<Shape[]>(hasInitialContacts ? [] : (props?.zones ?? []).map((z: any) => {
        const type = z.type === "circle" ? "radius" : z.type
        if (type === "radius") {
            const point = Array.isArray(z.point) ? z.point : z.point?.coordinates
            return { type: "radius", point, radius: z.radius } as Shape
        }
        const polygon = Array.isArray(z.polygon) ? z.polygon : z.polygon?.coordinates
        return { type: "polygon", polygon } as Shape
    }));
    const [zoneTimeRange, setZoneTimeRange] = useState<ZoneTimeRange>(props?.zoneTimeRange ?? {
        mode: "relative",
        pastValue: 7,
        pastUnit: "days",
        futureValue: 0,
        futureUnit: "days",
    });

    const [showSaveAsTemplate, setShowSaveAsTemplate] = useState<boolean>(false)
    const [templateName, setTemplateName] = useState<string>("")
    const [savingTemplate, setSavingTemplate] = useState<boolean>(false)


    const [useDefaultBroadcastConfig, setUseDefaultBroadcastConfig] = useState<boolean>(props?.useDefaultBroadcastConfig ?? true)
    const [useDefaultDeliveryMethods, setUseDefaultDeliveryMethods] = useState<boolean>(
        props?.useDefaultDeliveryMethods ?? (props?.deliveryMethods && props.deliveryMethods.length > 0 ? false : true)
    )

    const [deliveryMethodInterval, setDeliveryMethodInterval] = useState<number>(props?.broadcastConfig?.deliveryMethodInterval || 120)
    const [deliveryMethodIntervalDisplayFormat, setDeliveryMethodIntervalDisplayFormat] = useState<IntervalFormat>(props?.broadcastConfig?.deliveryMethodIntervalDisplayFormat || "minute")
    const [broadcastDuration, setBroadcastDuration] = useState<number>(props?.broadcastConfig?.broadcastDuration || 3600)
    const [broadcastDurationDisplayFormat, setBroadcastDurationDisplayFormat] = useState<IntervalFormat>(props?.broadcastConfig?.broadcastDurationDisplayFormat || "hour")
    const [cycleCount, setCycleCount] = useState<number>(props?.broadcastConfig?.cycleCount || 1)
    const [cycleInterval, setCycleInterval] = useState<number>(props?.broadcastConfig?.cycleInterval || 900)
    const [cycleIntervalDisplayFormat, setCycleIntervalDisplayFormat] = useState<IntervalFormat>(props?.broadcastConfig?.cycleIntervalDisplayFormat || "minute")

    const [preferAlphanumericSenderID, setPreferAlphanumericSenderID] = useState<boolean | undefined>(props?.smsSenderID === "alphanumeric" ? true : props?.smsSenderID === "long_code" ? false : undefined)
    const [skipContactsWithValidationErrors, setSkipContactsWithValidationErrors] = useState<boolean | undefined>(props?.skipContactsWithValidationErrors ?? false)


    const [validatingAlert, setValidatingAlert] = useState<boolean>(false)

    const [validationResults, setValidationResults] = useState<Record<string, number>>()
    const [validationToken, setValidationToken] = useState<string>()
    const [validationError, setValidationError] = useState<string>()
    const [validated, setValidated] = useState<boolean>(false)

    const [fileAttachmentIDs, setFileAttachmentIDs] = useState<string[]>(props?.fileAttachmentIDs ?? [])
    const [stagedUploads, setStagedUploads] = useState<StagedUpload[]>([])


    const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethod[]>((props?.deliveryMethods ?? []).map((m: any) => m.method ?? m))

    const [defaultDeliveryMethods, setDefaultDeliveryMethods] = useState<DeliveryMethod[]>()
    const [defaultBroadcastConfig, setDefaultBroadcastConfig] = useState<{
        broadcastDuration: number;
        broadcastDurationDisplayFormat: string;
        cycleCount: number;
        cycleInterval: number;
        cycleIntervalDisplayFormat: string;
        deliveryMethodInterval: number;
        deliveryMethodIntervalDisplayFormat: string;
    } | null>(null)


    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const golemErrors = useGolemErrors();

    const [sendImmediately, toggleSendImmediately, setSendImmediately] = useToggle(true)
    const [sendAt, setSendAt] = useState("")

    const {navigateWithClientID} = useNavigateWithClientID();

    const typeOptions = useRef([
        {value: "alert", label: "Alert"},
        {value: "message", label: "Message"},
    ])

    const speechModeOptions = useRef([
        {value: "", label: "None"},
        {value: "text_to_speech", label: "Text-to-Speech"},
        {value: "audio_file", label: "Audio File"},
    ])


    const {clientID, setToast} = useSiren()

    const [loading, setLoading] = useState(true)




    useEffect(() => {

        async function fetchData() {
            if(!clientID) return
            try {
                const [broadcastConfig, deliveryMethods] = await Promise.all([
                    resolveSirenDefaultBroadcastConfig(clientID, departmentID || undefined),
                    resolveSirenDefaultDeliveryMethods(clientID, departmentID || undefined),
                ])
                setDefaultBroadcastConfig(broadcastConfig)
                setDefaultDeliveryMethods((deliveryMethods?.deliveryMethods ?? []) as DeliveryMethod[])
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [clientID, departmentID]);

    const request = useMemo(() => {

        const alertFields = {
            responseOptions,
            zones,
            ...(zones.length > 0 ? { zoneTimeRange } : {}),
            smsResponseMode,
            contactIDs,
            contactGroupIDs,
            facilityIDs,
            useDefaultBroadcastConfig,
            useDefaultDeliveryMethods,
            ...(!useDefaultDeliveryMethods ? { deliveryMethods } : {}),
            ...(!useDefaultBroadcastConfig ? {
                broadcastConfig: {
                    deliveryMethodInterval,
                    deliveryMethodIntervalDisplayFormat,
                    broadcastDuration,
                    broadcastDurationDisplayFormat,
                    cycleCount,
                    cycleInterval,
                    cycleIntervalDisplayFormat,
                },
            } : {}),
        }

        const base = {
            name,
            category,
            content,
            type,
            clientID,
            departmentID: departmentID || null,
            speechMode,
            audioFileID,
            audioFileContainsIVR,
            useSeparateSMSContent,
            smsContent,
            fileAttachmentIDs,
            uploadIDs: stagedUploads.map(u => u.id),
            smsSenderID: preferAlphanumericSenderID === true ? "alphanumeric" : preferAlphanumericSenderID === false ? "long_code" : "",
        }

        if (type === "alert" || mode === "alert") {
            return {
                ...base,
                alert: alertFields,
                ...(mode === "alert" ? {
                    sendImmediately,
                    sendAt: toRFC3339WithOffset(sendAt),
                    skipContactsWithValidationErrors,
                    ...(props?.id ? { templateID: props.id, templateName: props.name } : {}),
                } : {}),
            }
        }

        return base

    }, [name,
        category,
        content,
        type,
        clientID,
        departmentID,
        responseOptions,
        zones,
        zoneTimeRange,
        useSeparateSMSContent,
        smsResponseMode,
        smsContent,
        speechMode,
        audioFileID,
        audioFileContainsIVR,
        contactIDs,
        contactGroupIDs,
        facilityIDs,
        deliveryMethodInterval,
        deliveryMethodIntervalDisplayFormat,
        broadcastDuration,
        broadcastDurationDisplayFormat,
        cycleCount,
        cycleInterval,
        cycleIntervalDisplayFormat,
        deliveryMethods,
        useDefaultBroadcastConfig,
        useDefaultDeliveryMethods,
        sendImmediately,
        sendAt,
        mode,
        fileAttachmentIDs,
        stagedUploads,
        preferAlphanumericSenderID,
        skipContactsWithValidationErrors,
    ])

    useEffect(() => {
        setValidated(false)
        setValidationResults(undefined)
        setValidationToken(undefined)
    }, [request])

    const validateAlert = useCallback(async () => {
        setValidatingAlert(true)
        setValidationError(undefined)
        setValidationResults(undefined)
        golemErrors.clearErrors()

        try {
            const res = await platformAPI.post<{counts: Record<string, number>, token: string}>("api/siren/alerts/validate", request)
            setValidationResults(res.data.counts)
            setValidationToken(res.data.token)
            setValidated(true)

            if (Object.keys(res.data.counts).length > 0) {
                const errs: Record<string, string[]> = {}
                for (const [key, count] of Object.entries(res.data.counts)) {
                    errs[key] = [`${count} contact(s) affected`]
                }
                golemErrors.setFromObject(errs)
            }
        } catch (error) {
            golemErrors.setFromAxiosError(error)
            if(isAxiosError(error) && error?.response?.data?.error){
                setValidationError(error.response.data.error)
            } else {
                setValidationError("An error has occurred")
            }
        }

        setValidatingAlert(false)


    }, [request])

    const create = useCallback(async () => {
        setIsLoading(true);
        golemErrors.clearErrors();

        try {
            await platformAPI.post("api/siren/templates", request)
            setToast("Mass Warning Template created", "success")
            navigateWithClientID(`/siren/templates?subPage=${request.type}`)

        } catch (e) {
            golemErrors.setFromAxiosError(e);
            if (isAxiosError(e) && e?.response?.data?.error) {
                setError(e.response.data.error);
            } else if (!isAxiosError(e) || !e?.response?.data?.errors) {
                setError("An error has occurred")
            }
        }

        setIsLoading(false);
    }, [request, golemErrors])


    const saveAsTemplate = useCallback(async () => {
        setSavingTemplate(true)
        try {
            const newRequest = {...request, name: templateName, type: "alert"}

            await platformAPI.post("api/siren/templates", newRequest)
            setShowSaveAsTemplate(false)

        } catch (e) {
            if (isAxiosError(e) && e?.response?.data?.error) {
                console.error(e.response.data.error);
            } else {
                console.error(e)
            }
        }
        setSavingTemplate(false)
    }, [request, templateName])

    const create_alert = useCallback(async () => {
        setIsLoading(true);
        golemErrors.clearErrors();

        try {
            const res = await platformAPI.post<{id: string}>("api/siren/alerts", request)
            setToast("Mass Warning Alert created", "success")
            navigateWithClientID(`/siren/alerts/${res.data.id}`)

        } catch (e) {
            golemErrors.setFromAxiosError(e);
            if (isAxiosError(e) && e?.response?.data?.error) {
                setError(e.response.data.error);
            } else if (!isAxiosError(e) || !e?.response?.data?.errors) {
                setError("An error has occurred")
            }
        }

        setIsLoading(false);
    }, [request, golemErrors])


    const update = useCallback(async () => {
        if (!props?.id) {
            return
        }

        setIsLoading(true);
        golemErrors.clearErrors();

        try {
            await platformAPI.patch(`api/siren/templates/${props.id}`, request)
            setToast("Mass Warning Template updated", "success")
            navigateWithClientID(`/siren/templates?subPage=${request.type}`)

        } catch (e) {
            golemErrors.setFromAxiosError(e);
            if (isAxiosError(e) && e?.response?.data?.error) {
                setError(e.response.data.error);
            } else if (!isAxiosError(e) || !e?.response?.data?.errors) {
                setError("An error has occurred")
            }
        }

        setIsLoading(false);
    }, [request, props?.id, golemErrors])


    return {
        name, setName,
        category, setCategory,
        content, setContent,
        useSeparateSMSContent, setUseSeparateSMSContent,
        smsResponseMode, setSMSResponseMode,
        smsContent, setSMSContent,
        type, setType,
        departmentID, setDepartmentID,
        responseOptions, setResponseOptions,
        zones, setZones,
        zoneTimeRange, setZoneTimeRange,
        speechMode, setSpeechMode,
        audioFileID, setAudioFileID,
        audioFileContainsIVR, setAudioFileContainsIVR,
        contactIDs, setContactIDs,
        contactGroupIDs, setContactGroupIDs,
        facilityIDs, setFacilityIDs,

        useDefaultBroadcastConfig, setUseDefaultBroadcastConfig,
        useDefaultDeliveryMethods, setUseDefaultDeliveryMethods,
        broadcastDuration, setBroadcastDuration,
        broadcastDurationDisplayFormat, setBroadcastDurationDisplayFormat,
        deliveryMethodInterval, setDeliveryMethodInterval,
        deliveryMethodIntervalDisplayFormat, setDeliveryMethodIntervalDisplayFormat,
        cycleCount, setCycleCount,
        cycleInterval, setCycleInterval,
        cycleIntervalDisplayFormat, setCycleIntervalDisplayFormat,
        deliveryMethods, setDeliveryMethods,
        sendImmediately, setSendImmediately,
        sendAt, setSendAt,

        showSaveAsTemplate, setShowSaveAsTemplate,
        templateName, setTemplateName,
        savingTemplate, setSavingTemplate,
        saveAsTemplate,

        typeOptions: typeOptions.current,
        speechModeOptions: speechModeOptions.current,
        isLoading,
        error,
        create,
        update,
        create_alert,
        validateAlert,
        validatingAlert,
        validationResults,
        validationToken,
        validationError,
        validated,
        loading,
        defaultDeliveryMethods,
        defaultBroadcastConfig,
        fileAttachmentIDs, setFileAttachmentIDs,
        stagedUploads, setStagedUploads,
        preferAlphanumericSenderID, setPreferAlphanumericSenderID,
        skipContactsWithValidationErrors, setSkipContactsWithValidationErrors,
        request,
        golemErrors,
    }


}